<script setup lang="ts">
import type { UserAvatarProps } from '@/components/user-avatar/user-avatar-props.js';
import { env } from '@/config/env.ts';
import { cn } from '@/lib/utils.ts';
import type { User } from '@/stores/auth.js';
import type { Participant } from '@/types/participant.js';
import { PAvatar, PAvatarFallback, PAvatarImage, PIconBadge } from '@pokerly/ui';
import { IconCheck, IconGhost3 } from '@tabler/icons-vue';
import { computed, useSlots, useTemplateRef } from 'vue';

const rootRef = useTemplateRef('root');

defineExpose({
    getElement: () => rootRef.value,
});

const props = withDefaults(defineProps<UserAvatarProps>(), { size: 'default' });
const slots = useSlots();

function getNameInitials(participant: Participant | User) {
    if (!participant.name) return '';
    const parts = participant.name?.split(' ').map((p) => p.charAt(0));
    const first = parts[0];
    const last = parts.length > 1 ? parts[parts.length - 1] : '';
    return [first, last].join('').toUpperCase();
}

const participantAvatarUrl = computed(() => {
    if (props.participant.profilePictureId) {
        return `${env.VITE_API_ROOT}/api/v1/user/${props.participant.id}/avatar/${props.participant.profilePictureId}`;
    }
    return null;
});

const badgeIcon = computed(() => {
    const participant = props.participant as Participant;
    if (participant.role === 'observer') {
        return IconGhost3;
    } else if (participant.role === 'voter' && participant.vote) {
        return IconCheck;
    }
    return null;
});

const badgeIconOffset = computed(() => {
    switch (props.size) {
        case 'default':
            return 'translate-x-2/5 -translate-y-2/6';
        case 'xl':
        case 'lg':
            return 'translate-x-1/5 -translate-y-1/5';
        case 'sm':
            return 'translate-x-1/3 -translate-y-1/3';
        default:
            return 'translate-x-1/2 -translate-y-1/2';
    }
});
</script>

<template>
    <div ref="root" class="inline-block relative">
        <PAvatar :class="props.class" :variant="variant" :size="size">
            <PAvatarImage v-if="participantAvatarUrl" :src="participantAvatarUrl" />
            <PAvatarFallback>
                <template v-if="slots.default">
                    <slot />
                </template>
                <template v-else>
                    {{ getNameInitials(participant) }}
                </template>
            </PAvatarFallback>
        </PAvatar>
        <PIconBadge
            v-if="badgeIcon && !hideBadge"
            variant="outline"
            :class="cn('absolute top-0 right-0 border ', badgeIconOffset)"
        >
            <component :is="badgeIcon" />
        </PIconBadge>
    </div>
</template>
